import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'flowbite-react';
import { Button } from '../button/button';
import { Text } from '../text/text';
import { type ComponentSize } from '../select/__commons__/selectCommons';
import { modalTheme } from '../../styles/themes/modal';

export type OverXlSize = '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
export type EveryComponentSize = ComponentSize | OverXlSize;
export type ModalVariant = 'default' | 'destruct';

type ExtraButton = {
    label: string;
    action: () => void;
    disabled?: boolean;
};

export type ModalProps = {
    className?: string;
    id?: string;
    testId?: string;
    ariaLabel?: string;
    size?: EveryComponentSize;
    variant?: ModalVariant;
    openModal: boolean;
    dismissible?: boolean;
    handleClose: () => void;
    handleCancel: () => void;
    handleConfirm: () => void;
    cancelDisabled?: boolean;
    confirmDisabled?: boolean;
    title: string;
    customCancelLabel?: string;
    customConfirmLabel?: string;
    extraButton?: ExtraButton;
    bodyHeight?: number;
    children: React.ReactNode;
};

export const ModalComponent: React.FC<ModalProps> = (props: ModalProps) => {
    const {
        className,
        id,
        testId,
        ariaLabel,
        size = 'md',
        variant = 'default',
        openModal,
        dismissible = true,
        handleClose,
        handleCancel,
        handleConfirm,
        cancelDisabled = false,
        confirmDisabled = false,
        customCancelLabel,
        customConfirmLabel,
        title,
        extraButton,
        bodyHeight,
        children,
    } = props;

    const destructMode = variant === 'destruct';

    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

    useEffect(() => {
        if (!openModal) return;

        const timer = setTimeout(() => {
            const el = scrollRef.current;
            if (el && bodyHeight) {
                setHasVerticalScroll(el.scrollHeight > bodyHeight);
            }
        }, 0); // runs after next paint

        return () => clearTimeout(timer);
    }, [bodyHeight, openModal]);
    return (
        /* Modal includes the screen-filling layer outside the dialog border */
        <Modal
            theme={modalTheme} // text and bg classes defined in theme
            className={className}
            show={openModal}
            size={destructMode ? 'md' : size}
            onClose={handleClose}
            id={id}
            data-testid={testId}
            aria-label={ariaLabel}
            role='dialog'
            dismissible={dismissible}
        >
            <ModalHeader data-testid={'modal-header'}>
                <Text variant='title-medium'>{title}</Text>
            </ModalHeader>
            <div ref={scrollRef}>
                {/* 280/744 (from design) - 160 (for header + footer) */}
                <ModalBody
                    data-testid={'modal-body'}
                    style={{
                        minHeight: '120px',
                        maxHeight: '584px',
                        ...(!!bodyHeight ? { height: `${bodyHeight}px` } : {}),
                    }}
                    className={`space-y-4 ${hasVerticalScroll ? 'border-y border-system-outline-variant' : ''}`}
                >
                    {/* content will scroll if overflowing the (optional) set height */}
                    {children}
                </ModalBody>
            </div>
            <ModalFooter>
                {/* buttons row */}
                <div className='w-full flex flex-row justify-between gap-4'>
                    {extraButton && (
                        <Button
                            data-testid={'modal-extra-button'}
                            onClick={extraButton.action}
                            label={extraButton.label}
                            size='sm'
                            color='ghost'
                            className={'px-3'}
                            disabled={!!extraButton.disabled}
                        />
                    )}
                    <div className='w-full flex flex-row justify-end gap-4'>
                        <Button
                            data-testid={'modal-cancel-button'}
                            className=''
                            onClick={handleCancel}
                            label={customCancelLabel ?? 'Cancel'}
                            size='sm'
                            color='outline'
                            disabled={cancelDisabled}
                        />
                        <Button
                            data-testid={'modal-confirm-button'}
                            onClick={handleConfirm}
                            label={customConfirmLabel ?? (destructMode ? 'Destruct' : 'Confirm')}
                            size='sm'
                            color={destructMode ? 'destruct' : 'primary'}
                            disabled={confirmDisabled}
                        />
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};
